import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, ChevronLeft, CornerUpRight, MessageCircleOff, TriangleAlert, X } from 'lucide-react';
import { decideBot, type BotDecision } from '@/api/bots';
import {
  createLead, disqualifyDeal, getInboxPipelineId, moveDeal, setConversationStage, type CardStage, type LostReason,
} from '@/api/crmCard';
import { getConversation, getInboxCapabilities, listMessages } from '@/api/inbox';
import { composerRules } from '@/lib/channelRules';
import type { InboxConversation } from '@/api/inboxTypes';
import { Avatar } from '@/components/inbox/Avatar';
import { EmptyState } from '@/components/EmptyState';
import { Sheet } from '@/components/Sheet';
import { BotsSheet } from '@/components/chat/BotsSheet';
import { CallOverlay } from '@/components/chat/CallOverlay';
import { Composer } from '@/components/chat/Composer';
import { DealSheet } from '@/components/chat/DealSheet';
import { DealStrip } from '@/components/chat/DealStrip';
import { ForwardSheet } from '@/components/chat/ForwardSheet';
import { MessageList } from '@/components/chat/MessageList';
import { QuickTaskSheet } from '@/components/chat/QuickTaskSheet';
import { StageSheet } from '@/components/chat/StageSheet';
import { useCallFlow } from '@/hooks/useCallFlow';
import { useCwSession } from '@/hooks/useCwSession';
import { useDealCard } from '@/hooks/useDealCard';
import { useInboxes } from '@/hooks/useInboxData';
import { channelKind } from '@/lib/channels';
import { cwKeys, findConversation, putConversation } from '@/lib/cwCache';
import { phoneFor, stageRoute, stripModel } from '@/lib/dealCard';
import { changeStage } from '@/lib/stageChange';
import { mergeLatest, oldestServerId, prependOlder, type MessagesState } from '@/lib/messageStore';
import { setOpenConversation } from '@/lib/openConversation';
import { markRead } from '@/lib/readMarker';
import { discard, retry, send, sendFiles, type FilesInput, type SendContext } from '@/lib/sendMessage';
import { ApiError } from '@/lib/http';
import { useBackLayer } from '@/lib/backStack';
import { orderedSelection, forwardSummary, summaryDuration, toggleSelection, MAX_FORWARD, type ForwardResult } from '@/lib/forward';
import { tapLight } from '@/lib/haptics';
import { useToast } from '@/store/toast';

/**
 * Экран переписки. Адрес всегда несёт номер кабинета: display_id уникален только внутри кабинета.
 * Рисуется поверх списка «Входящие» (см. InboxPage → Outlet), поэтому список под ним остаётся живым.
 */
export default function ConversationPage() {
  const params = useParams();
  const accountId = Number(params.accountId);
  const displayId = Number(params.displayId);
  if (!Number.isInteger(accountId) || !Number.isInteger(displayId) || accountId <= 0 || displayId <= 0) {
    return <Navigate to="/inbox" replace />;
  }
  return <ConversationView accountId={accountId} convId={displayId} />;
}

function ConversationView({ accountId, convId }: { accountId: number; convId: number }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast((t) => t.show);
  const session = useCwSession();
  const myUserId = session.myUserId;
  const myName = session.profile.data?.name ?? '';
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Окно сделки открыто сразу: из «Воронки» (state.openDeal) или из «Звонков»/«Календаря» кнопкой «Открыть сделку» (?sheet=deal).
  const [sheet, setSheet] = useState<null | 'deal' | 'stage' | 'task' | 'bots'>(
    () => ((location.state as { openDeal?: boolean } | null)?.openDeal
      || new URLSearchParams(location.search).get('sheet') === 'deal' ? 'deal' : null),
  );
  const [creating, setCreating] = useState(false);
  // Режим выбора для пересылки: null — обычная переписка, иначе номера отмеченных сообщений.
  const [selected, setSelected] = useState<Set<number> | null>(null);
  const [forwarding, setForwarding] = useState(false);
  // Системная «назад» сначала закрывает окно пересылки (оно регистрирует себя само), затем выходит из выбора.
  useBackLayer(() => setSelected(null), selected !== null);

  // Шапка: из кэша списков — мгновенно, затем уточняем запросом.
  const convQ = useQuery<InboxConversation>({
    queryKey: cwKeys.conv(accountId, convId),
    queryFn: () => getConversation(accountId, convId),
    placeholderData: () => findConversation(qc, accountId, convId),
    staleTime: 60_000,
    retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2,
  });
  const conv = convQ.data;

  // Лента: свежие 20 поверх уже загруженного (свои неотправленные и подгруженные старые не теряются).
  const msgQ = useQuery<MessagesState>({
    queryKey: cwKeys.msgs(accountId, convId),
    queryFn: async () => mergeLatest(qc.getQueryData<MessagesState>(cwKeys.msgs(accountId, convId)), await listMessages(accountId, convId)),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });

  const inboxesQ = useInboxes(accountId, session.enabled);

  // Правила канала для строки ввода (голосовые, виды файлов, по одному ли). Кэш на канал; страница
  // Facebook — на переписку (её Instagram Direct живёт по правилам Instagram). Нет ответа — как раньше.
  const capsInbox = conv?.inboxId ?? 0;
  const capsConv = conv?.channelType === 'Channel::FacebookPage' ? convId : 0;
  const capsQ = useQuery({
    queryKey: cwKeys.caps(accountId, capsInbox, capsConv),
    queryFn: () => getInboxCapabilities(accountId, capsInbox, capsConv || undefined),
    enabled: session.enabled && capsInbox > 0,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    retry: 1,
  });
  const rules = useMemo(() => composerRules(capsQ.data ?? null, conv?.channelType), [capsQ.data, conv?.channelType]);

  // Сделка CRM по этой переписке и звонок из неё (этап 2b).
  const cardQ = useDealCard(conv, accountId, session.enabled);
  const card = cardQ.data;
  const deal = card?.deal ?? null;
  const call = useCallFlow({
    dealId: deal?.id,
    personId: deal?.person?.id ?? card?.person?.id,
    name: conv?.contact.name ?? '',
  });

  // Эта переписка открыта: события реального времени отмечают её прочитанной.
  useEffect(() => {
    setOpenConversation({ accountId, id: convId });
    return () => setOpenConversation(null);
  }, [accountId, convId]);

  // Прочитано: при открытии и при возврате в приложение.
  useEffect(() => {
    if (!msgQ.isSuccess) return;
    const mark = () => { if (document.visibilityState === 'visible') markRead(qc, accountId, convId, myUserId); };
    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [qc, accountId, convId, myUserId, msgQ.isSuccess]);

  const ctx: SendContext = useMemo(
    () => ({ qc, accountId, convId, myUserId, myName }),
    [qc, accountId, convId, myUserId, myName],
  );

  const onSend = useCallback((text: string, isNote: boolean) => { send(ctx, text, isNote); }, [ctx]);
  const onSendFiles = useCallback((input: FilesInput) => { sendFiles(ctx, input); }, [ctx]);
  const onDecide = useCallback(async (invocationId: number, decision: BotDecision) => {
    try {
      await decideBot(accountId, invocationId, decision);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось передать решение');
      throw e;
    }
  }, [accountId, toast]);
  const onLongPress = useCallback((id: number) => {
    void tapLight();
    setSelected(new Set([id]));
  }, []);
  const onToggle = useCallback((id: number) => {
    setSelected((cur) => {
      if (!cur) return cur;
      const r = toggleSelection(cur, id);
      if (r.limited) toast(`За раз можно переслать не больше ${MAX_FORWARD} сообщений`);
      return r.next;
    });
  }, [toast]);
  const onRetry = useCallback((echoId: string) => retry(ctx, echoId), [ctx]);
  const onDiscard = useCallback((echoId: string) => discard(ctx, echoId), [ctx]);

  const onLoadOlder = useCallback(async () => {
    const state = qc.getQueryData<MessagesState>(cwKeys.msgs(accountId, convId));
    const before = state ? oldestServerId(state) : null;
    if (!state || before === null) return;
    setLoadingOlder(true);
    try {
      const older = await listMessages(accountId, convId, before);
      qc.setQueryData<MessagesState>(cwKeys.msgs(accountId, convId), (s) => (s ? prependOlder(s, older) : s));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось загрузить');
    } finally {
      setLoadingOlder(false);
    }
  }, [qc, accountId, convId, toast]);

  // Ушло: закрываем выбор и даём перейти в переписку, куда переслали.
  const forwarded = (r: ForwardResult) => {
    setForwarding(false);
    setSelected(null);
    toast(forwardSummary(r), summaryDuration(r), {
      label: 'Открыть',
      run: () => navigate(`/inbox/${accountId}/${r.conversation_id}`),
    });
  };

  const back = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/inbox');
  };

  const inbox = inboxesQ.data?.find((i) => i.id === conv?.inboxId);
  const kind = channelKind(conv?.channelType);
  const inboxLine = !conv ? '' : !inbox || inbox.name.toLowerCase().startsWith(kind.label.toLowerCase())
    ? (inbox?.name ?? kind.label)
    : `${kind.label} · ${inbox.name}`;
  const strip = stripModel(card, { loading: cardQ.isPending, error: cardQ.isError ? cardQ.error.message : null });
  const phone = phoneFor(conv?.contact.phone, card);

  /** Записать новую стадию и у самой переписки: цвет и подпись в ленте обновятся без ожидания события. */
  const applyStageToConversation = (st: CardStage) => {
    if (conv) putConversation(qc, accountId, { ...conv, crmStageId: st.id }, myUserId);
  };

  const route = deal && conv ? stageRoute(deal, convId, { crmDealId: conv.crmDealId, crmStageId: conv.crmStageId }) : 'crm';

  const pickStage = async (st: CardStage) => {
    setSheet(null);
    if (!deal) return;
    const r = await changeStage({
      qc, cardKey: cardQ.key, stage: st, route, dealId: deal.id,
      onApplied: applyStageToConversation,
      viaChatwoot: (id) => setConversationStage(accountId, convId, id),
      viaCrm: (dealId, id) => moveDeal(dealId, id),
    });
    toast(r.ok ? `Стадия изменена: ${st.name}` : r.error, r.ok ? 2200 : 5000);
  };

  // Отказ идёт только через CRM: Chatwoot-путь причину отказа не передаёт.
  const loseDeal = async (st: CardStage, reason: LostReason, comment: string) => {
    setSheet(null);
    if (!deal) return;
    const r = await changeStage({
      qc, cardKey: cardQ.key, stage: st, route: 'crm', dealId: deal.id,
      onApplied: applyStageToConversation,
      viaChatwoot: async () => undefined,
      viaCrm: (dealId, id) => disqualifyDeal(dealId, { reasonId: reason.id, ...(comment ? { comment } : {}), stageId: id }),
    });
    toast(r.ok ? 'Сделка закрыта с отказом' : r.error, r.ok ? 2200 : 5000);
  };

  const makeLead = async () => {
    if (!conv || creating) return;
    setCreating(true);
    try {
      const pipelineId = await getInboxPipelineId(accountId, conv.inboxId).catch(() => null);
      await createLead({
        name: conv.contact.name, phone: conv.contact.phone, email: conv.contact.email,
        chatwootContactId: conv.contact.id || undefined, chatwootAccountId: accountId, displayId: convId, pipelineId,
      });
      await cardQ.refetch();
      toast('Лид заведён');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Не удалось завести лид', 5000);
    } finally {
      setCreating(false);
    }
  };

  // Переписка недоступна или не найдена.
  if (convQ.isError && !conv) {
    const notFound = convQ.error instanceof ApiError && [403, 404].includes(convQ.error.status);
    return (
      <div className="conv-layer">
        <div className="chdr">
          <button type="button" className="ibtn" onClick={back} aria-label="Назад"><ChevronLeft aria-hidden /></button>
        </div>
        <EmptyState
          icon={notFound ? MessageCircleOff : TriangleAlert}
          title={notFound ? 'Переписка не найдена' : 'Переписка не загрузилась'}
          text={notFound ? 'Возможно, её удалили или у вас нет к ней доступа.' : convQ.error.message}
          action={notFound ? { label: 'К списку', onClick: back } : { label: 'Повторить', onClick: () => void convQ.refetch() }}
        />
      </div>
    );
  }

  const state = msgQ.data;
  const selectedIds = selected ? orderedSelection(state?.messages ?? [], selected) : [];
  return (
    <div className="conv-layer" role="region" aria-label={conv ? `Переписка с ${conv.contact.name}` : 'Переписка'}>
      {selected ? (
        <div className="chdr">
          <button type="button" className="ibtn" onClick={() => setSelected(null)} aria-label="Отмена"><X aria-hidden /></button>
          <span className="fwd-hdr" aria-live="polite">Выбрано: <span className="tnum">{selectedIds.length}</span></span>
          <button type="button" className="fwd-hdr-btn" disabled={selectedIds.length === 0 || !conv} onClick={() => setForwarding(true)}>
            <CornerUpRight aria-hidden />Переслать
          </button>
        </div>
      ) : (
      <div className="chdr">
        <button type="button" className="ibtn" onClick={back} aria-label="Назад"><ChevronLeft aria-hidden /></button>
        {conv && <Avatar name={conv.contact.name} seed={conv.contact.id || conv.id} channelType={conv.channelType} />}
        <span className="ct">
          <b>{conv?.contact.name ?? 'Загрузка…'}</b>
          <span>{inboxLine}</span>
        </span>
        <button type="button" className="ibtn" aria-label="Позвать помощника" onClick={() => setSheet('bots')}>
          <Bot aria-hidden />
        </button>
      </div>
      )}
      <DealStrip
        model={strip}
        creating={creating}
        calling={call.starting}
        onOpenStage={() => setSheet('stage')}
        onOpenCard={() => setSheet('deal')}
        onCall={() => void call.start(phone)}
        onCreateLead={() => void makeLead()}
        onRetry={() => void cardQ.refetch()}
      />
      {msgQ.isPending && !state ? (
        <div className="scr flex items-center justify-center" role="status" aria-label="Загрузка сообщений">
          <span className="spinner size-9" />
        </div>
      ) : msgQ.isError && !state ? (
        <div className="scr">
          <EmptyState icon={TriangleAlert} title="Сообщения не загрузились" text={msgQ.error.message} action={{ label: 'Повторить', onClick: () => void msgQ.refetch() }} />
        </div>
      ) : (
        <MessageList
          messages={state?.messages ?? []}
          hasMore={state?.hasMore ?? false}
          loadingOlder={loadingOlder}
          onLoadOlder={() => void onLoadOlder()}
          myUserId={myUserId}
          onRetry={onRetry}
          onDiscard={onDiscard}
          onDecide={onDecide}
          selected={selected}
          onLongPress={onLongPress}
          onToggle={onToggle}
          empty={<EmptyState icon={MessageCircleOff} title="Сообщений пока нет" text="Первое сообщение ещё не пришло. Оно появится здесь само." />}
        />
      )}
      {!selected && <Composer canReply={conv?.canReply ?? true} onSend={onSend} onSendFiles={onSendFiles} onToast={toast} rules={rules} />}

      {forwarding && conv && selectedIds.length > 0 && (
        <ForwardSheet accountId={accountId} source={conv} messageIds={selectedIds} onClose={() => setForwarding(false)} onDone={forwarded} />
      )}

      {sheet === 'deal' && card && (
        <DealSheet
          project={cardQ.project}
          card={card}
          contactName={conv?.contact.name ?? ''}
          contactPhone={phone}
          onClose={() => setSheet(null)}
          onStage={() => setSheet('stage')}
          onCall={() => { setSheet(null); void call.start(phone); }}
          onTask={() => setSheet('task')}
          onMeeting={() => toast('Встречи появятся на следующем этапе (этап 4)')}
        />
      )}
      {sheet === 'stage' && deal && card && (
        <StageSheet
          project={cardQ.project}
          pipelineName={deal.pipeline?.name ?? ''}
          stages={card.stages ?? []}
          currentId={deal.stageId}
          onPick={(st) => void pickStage(st)}
          onLost={(st, reason, comment) => void loseDeal(st, reason, comment)}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'task' && deal && (
        <QuickTaskSheet project={cardQ.project} dealId={deal.id} pipelineId={deal.pipelineId} onClose={() => setSheet(null)} />
      )}
      {sheet === 'bots' && (
        <BotsSheet accountId={accountId} convId={convId} messages={state?.messages ?? []} onClose={() => setSheet(null)} />
      )}

      {call.notice && (
        <Sheet label="Звонок" onClose={call.closeNotice}>
          <div className="sh"><h3>Позвонить</h3></div>
          <div className="sb">
            <p className="pnote" style={{ fontSize: 15, color: 'var(--fg)' }}>{call.notice.text}</p>
            <div className="btnrow">
              {call.notice.tel && <a className="primary" href={call.notice.tel} onClick={call.closeNotice}>Позвонить с телефона</a>}
              <button type="button" className="ghost" onClick={call.closeNotice}>Понятно</button>
            </div>
          </div>
        </Sheet>
      )}
      {call.ui && <CallOverlay call={call.ui} seed={conv?.contact.id || convId} channelType={conv?.channelType} onClose={call.close} />}
    </div>
  );
}
